<template>
	<button
		ref="button"
		v-bind="attrsWithoutClass"
		:type="submit ? 'submit' : 'button'"
		:class="buttonClasses"
		@click="$emit('click', $event)"
		:tabindex="
			tabindex !== null && tabindex !== undefined
				? tabindex
				: preventFocus
				? '-1'
				: null
		"
		:disabled="disabled"
		:aria-label="ariaLabel"
	>
		<Component
			:is="iconComponent"
			v-if="iconComponent"
			:size="iconSize || 16"
			class="shrink-0"
		/>
		<Icon
			v-else-if="icon"
			:icon="[iconPrefix, icon]"
			:size="iconSize"
			class="shrink-0"
		/>
		<template v-if="hasDefaultSlot && !isIconOnlyButton">
			<span class="flex min-w-0 items-center truncate">
				<slot />
			</span>
		</template>
		<template v-else-if="hasDefaultSlot">
			<slot />
		</template>
		<Icon
			v-if="iconAfter"
			:icon="[iconPrefix, iconAfter]"
			:size="iconAfterSize"
			class="shrink-0"
		/>
	</button>
</template>

<script>
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/ui/common/Icon'

export default {
	name: 'Button',
	components: {
		Icon
	},
	inheritAttrs: false,
	props: {
		type: {
			type: String,
			default: 'primary',
			validator: value =>
				[
					'primary',
					'secondary',
					'secondary-dark',
					'tertiary',
					'danger',
					'danger-outline',
					'danger-light',
					'success',
					'link',
					'light'
				].includes(value)
		},
		icon: {
			type: String,
			default: null
		},
		iconComponent: {
			type: [Object, Function, String],
			default: null
		},
		iconAfter: {
			type: String,
			default: null
		},
		iconPrefix: {
			type: String,
			default: 'far'
		},
		iconSize: {
			type: String,
			default: null
		},
		iconAfterSize: {
			type: String,
			default: null
		},
		iconOnly: {
			type: [Boolean, String],
			default: undefined
		},
		submit: {
			type: Boolean,
			default: false
		},
		block: {
			type: Boolean,
			default: false
		},
		preventFocus: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		size: {
			type: String,
			default: null,
			validator: value => !value || ['sm', 'lg'].includes(value)
		},
		ariaLabel: {
			type: String,
			default: null
		},
		tabindex: {
			type: [Number, String],
			default: null
		}
	},

	computed: {
		// Exclude 'class' from $attrs to prevent duplicate class application
		attrsWithoutClass() {
			const { class: _, ...attrs } = this.$attrs
			return attrs
		},

		externalClassList() {
			const data = this.$vnode?.data || {}
			const classes = []

			if (data.staticClass) {
				classes.push(data.staticClass)
			}

			if (data.class) {
				classes.push(this.normalizeDynamicClass(data.class))
			}

			return classes
				.filter(Boolean)
				.join(' ')
				.trim()
		},

		hasDefaultSlot() {
			return Boolean(this.$slots.default)
		},

		isIconOnlyButton() {
			if (typeof this.iconOnly !== 'undefined') {
				return ![false, 'false'].includes(this.iconOnly)
			}

			return (
				(this.icon || this.iconComponent || this.iconAfter) &&
				!this.hasDefaultSlot
			)
		},

		buttonClasses() {
			const componentClasses = [
				'inline-flex items-center justify-center font-normal rounded-lg transition-colors duration-200',
				'min-w-0 max-w-full',
				'focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0',
				this.sizeClasses,
				'gap-2',
				this.typeClasses,
				this.block ? 'w-full' : '',
				this.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
			]
				.filter(Boolean)
				.join(' ')

			return twMerge(componentClasses, this.externalClassList)
		},

		sizeClasses() {
			if (this.size === 'sm') {
				return this.isIconOnlyButton
					? 'h-8 w-8 text-sm'
					: 'h-8 px-2.5 text-sm'
			} else if (this.size === 'lg') {
				return this.isIconOnlyButton
					? 'h-12 w-12 text-lg'
					: 'h-12 px-4.5 text-lg'
			} else {
				return this.isIconOnlyButton
					? 'h-10 w-10 text-base'
					: 'h-10 px-3.5 text-base'
			}
		},

		typeClasses() {
			const types = {
				primary:
					'bg-[#303030] text-white hover:bg-[#3D3D3D] active:bg-[#1A1A1A] disabled:bg-[#838383]',
				secondary:
					'bg-white text-[#303030] border border-[#E3E3E3] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] active:border-[#D0D0D0]',
				'secondary-dark':
					'bg-transparent text-white border border-grey-300 hover:bg-grey-950 active:bg-grey-900 disabled:border-grey-500 disabled:text-grey-500',
				tertiary:
					'bg-transparent text-[#303030] hover:bg-[#F5F5F5] active:bg-[#E5E5E5]',
				danger:
					'bg-[#BA0000] text-white hover:bg-[#9A0000] active:bg-[#922107] disabled:bg-[#E08C8C]',
				'danger-outline':
					'bg-white text-[#BA0000] border border-[#BA0000] hover:bg-[#FFF5F5] hover:border-[#A00000] active:bg-[#FFEAE8] active:border-[#900000]',
				'danger-light':
					'bg-[#FDE2DD] text-[#732819] hover:bg-[#FDD6CE] active:bg-[#FBCABE]',
				success:
					'bg-[#E6F4EC] text-[#1E442E] hover:bg-[#DCEFE4] active:bg-[#D0E8DA]',
				link: 'bg-transparent',
				light:
					'bg-white hover:text-gray-800 transition-shadow duration-300 shadow-transparent hover:shadow-lg'
			}

			return types[this.type] || types.primary
		}
	},

	methods: {
		focus() {
			this.$refs.button.focus()
		},

		normalizeDynamicClass(value) {
			if (Array.isArray(value)) {
				return value
					.map(v => this.normalizeDynamicClass(v))
					.filter(Boolean)
					.join(' ')
			}

			if (typeof value === 'object' && value !== null) {
				return Object.keys(value)
					.filter(key => value[key])
					.join(' ')
			}

			return typeof value === 'string' ? value : ''
		}
	}
}
</script>
